import { zodResolver } from "@hookform/resolvers/zod";
import { type Control, Controller, useForm } from "react-hook-form";
import type { z } from "zod/v3";
import { isWrongPassword } from "@/api/security";
import { PasswordInput } from "@/components/base/input/password-input";
import { globalHandleError } from "@/providers/QueryClientProviderWrapper";
import { passwordSchema, WRONG_PASSWORD } from "@/utils/two-factor";

type PasswordValues = z.infer<typeof passwordSchema>;

export const usePasswordConfirmation = (onConfirm: (password: string) => Promise<void>) => {
  const form = useForm<PasswordValues>({ resolver: zodResolver(passwordSchema), defaultValues: { password: "" } });

  const submit = form.handleSubmit(async ({ password }) => {
    try {
      await onConfirm(password);
    } catch (err) {
      if (isWrongPassword(err)) form.setError("password", { message: WRONG_PASSWORD });
      else globalHandleError(err);
    }
  });

  return { form, submit };
};

export const PasswordField = ({
  control,
  label,
  autoFocus,
}: {
  control: Control<PasswordValues>;
  label: string;
  autoFocus?: boolean;
}) => (
  <Controller
    control={control}
    name="password"
    render={({ field, fieldState }) => (
      <PasswordInput
        label={label}
        placeholder="Enter your password"
        autoComplete="current-password"
        autoFocus={autoFocus}
        value={field.value}
        onChange={field.onChange}
        onBlur={field.onBlur}
        isInvalid={!!fieldState.error}
        hint={fieldState.error?.message}
      />
    )}
  />
);
